import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { outfitService, OutfitShare } from '../services/outfit';
import { Button } from '../components/common/Button';
import { getImageUrl } from '../utils/imageUrl';

export const ShareDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [share, setShare] = useState<OutfitShare | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadShare = async () => {
    try {
      const data = await outfitService.getShare(id!);
      setShare(data);
    } catch (err) {
      console.error('Failed to load share:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (feedbackType: 'like' | 'dislike') => {
    if (!id) return;

    setSubmitting(true);
    try {
      await outfitService.submitFeedback(id, {
        feedback_type: feedbackType,
        feedback_notes: feedbackNotes,
      });
      await loadShare();
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadShare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!share || !share.outfit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Share not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{share.outfit.name}</h1>
            <p className="text-gray-600">
              Shared by <span className="font-semibold">{share.sender?.username || 'Unknown'}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {share.outfit.items.map((item) => (
              <div key={item.id} className="aspect-square rounded-xl overflow-hidden">
                <img
                  src={getImageUrl(item.image_url)}
                  alt={item.category}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          {share.status === 'pending' && (
            <div className="space-y-4">
              <textarea
                value={feedbackNotes}
                onChange={(e) => setFeedbackNotes(e.target.value)}
                placeholder="Add your thoughts (optional)..."
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none"
                rows={4}
              />
              <div className="flex gap-4">
                <Button
                  variant="accent"
                  size="lg"
                  className="flex-1"
                  onClick={() => handleFeedback('like')}
                  disabled={submitting}
                >
                  👍 Like
                </Button>
                <Button
                  variant="danger"
                  size="lg"
                  className="flex-1"
                  onClick={() => handleFeedback('dislike')}
                  disabled={submitting}
                >
                  👎 Dislike
                </Button>
              </div>
            </div>
          )}

          {share.feedback_type && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <p className="font-semibold mb-2">
                Your feedback: {share.feedback_type === 'like' ? '👍 Liked' : '👎 Disliked'}
              </p>
              {share.feedback_notes && (
                <p className="text-gray-700">{share.feedback_notes}</p>
              )}
            </div>
          )}

          <div className="mt-6">
            <Button variant="secondary" onClick={() => navigate('/shares')}>
              Back to Shares
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

